import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { dayAndTimeToDate } from '@/App/tiles/orders/utils.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { mapOrderTypeToWebApiPathComponent } from '@/store/orders/algoOrders/helper.ts';
import type { AlgoOrderRequest, Speed } from '@/store/orders/algoOrders/state.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { getDateNow } from '@/utils/date.ts';
import { toBase64 } from '@/utils/toBase64.ts';

type BaseSubmitAlgoOrderBody = {
  isGtc: boolean;
  bdrId: string;
  amountInCcy1: number | undefined;
  amountInCcy2: number | undefined;
  ccyPair: string;
  way: 'buy' | 'sell';
  base64Emails: string;
  localTimeWithOffSet: number;
  maturityDateString?: string;
  startDateUtc?: string;
  endDateUtc?: string;
  limitPrice?: string;
  isNdf: boolean;
  liquidityPool: string;
};

type SubmitTwapOrderBody = BaseSubmitAlgoOrderBody & {
  clipSize?: number;
  spreadCapture: boolean;
  clippingMode: string;
  dodging: boolean;
};

type SubmitFalconOrderBody = BaseSubmitAlgoOrderBody & {
  speed: Speed;
};

type SubmitNightjarOrderBody = BaseSubmitAlgoOrderBody & {
  speed: Speed;
  alphaSeeker: boolean;
};

export type SubmitAlgoOrderBody = SubmitTwapOrderBody | SubmitFalconOrderBody | SubmitNightjarOrderBody;

export const submitAlgo = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, AlgoOrderRequest>({
    query: (request) => {
      return {
        url: `/oms/submit/${mapOrderTypeToWebApiPathComponent(request.orderType)}`,
        method: 'POST',
        body: generateBody(request),
        timeout: 10000,
        headers: {
          // without this header, the response is a string
          // instead of JSON, even if the value is "application/json"
          accept: '*/*',
        },
      };
    },
    transformResponse: (
      response:
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          },
    ) => {
      return {
        type: 'success',
        orderId: response as string, // assuming the response is a string with the order ID
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
            response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );
        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }
        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (order: AlgoOrderRequest): SubmitAlgoOrderBody => {
  const commonBody = {
    isGtc: order.expiryDate.goodTillDate,
    bdrId: String(order.clientId),
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    way: order.way ?? 'buy',
    amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
    amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,

    liquidityPool: order.liquidityPool.length > 0 ? order.liquidityPool.join(',') : '',
    isNdf: order.isNdf,
    maturityDateString: !order.isNdf ? order.tenor : undefined,
    startDateUtc: order.startDate.isNow
      ? getDateNow().toISOString()
      : dayAndTimeToDate(order.startDate.date, order.startDate.time),
    endDateUtc: !order.expiryDate.goodTillDate
      ? dayAndTimeToDate(order.expiryDate.date, order.expiryDate.time)
      : undefined,
    limitPrice: isDefined(order.limitPrice) ? String(order.limitPrice) : undefined,
    base64Emails: isDefined(order?.notificationEmails?.emails)
      ? toBase64(order?.notificationEmails.emails.join(';'))
      : '',
    localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
  };

  switch (order.orderType) {
    case 'Twap+Order':
      return {
        ...commonBody,
        clippingMode: order.clippingMode,
        spreadCapture: order.spreadCapture,
        clipSize: order.clippingMode === 'Manual' ? order.clipSize : undefined,
        dodging: order.randomize,
      };
    case 'FalconOrder':
      return {
        ...commonBody,
        speed: order.speed,
      };
    case 'NightjarOrder':
      return {
        ...commonBody,
        speed: order.speed,
        alphaSeeker: order.alphaSeeker,
      };
    default: {
      const exhaustiveCheck: never = order;
      return exhaustiveCheck as SubmitAlgoOrderBody;
    }
  }
};

function isValidateOrderResponse(response: unknown): response is {
  validationResponseDetailContent: ValidationResponseDetailContent[];
  isReadyToSubmit: boolean;
} {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
